import { styled, keyframes } from 'styled-components';

/* ─────────────────────── Animations ─────────────────────── */

const fadeIn = keyframes`
    from { opacity: 0; transform: translate(-50%, -50%) scale(0.95); }
    to   { opacity: 1; transform: translate(-50%, -50%) scale(1); }
`;

const slideDown = keyframes`
    from { opacity: 0; transform: translate(-50%, -8px); }
    to   { opacity: 1; transform: translate(-50%, 0); }
`;

const spin = keyframes`
    to { transform: rotate(360deg); }
`;

const fadeOverlay = keyframes`
    from { opacity: 0; }
    to   { opacity: 1; }
`;

/* ─────────────────────── Table Wrapper ──────────────────── */

export const DivTableWrapper = styled.div`
    width: 100%;
    position: relative;
    overflow-x: auto;
    display: flex;
    justify-content: flex-start;
    padding: 0.5rem 0;
`;

/* ─────────────────────── Table ──────────────────────────── */

export const TableGroups = styled.table`
    border-collapse: separate;
    border-spacing: 0;
    border: 1px solid ${p => p.theme.border};
    border-radius: ${p => p.theme.radius.md};
    overflow: hidden;
    background-color: ${p => p.theme.surface};
    box-shadow: ${p => p.theme.shadowSm};
    flex-shrink: 0;

    & thead tr {
        background: ${p => p.theme.surface2};
    }

    & th {
        position: relative;
        padding: 0.65rem 0.75rem;
        font-size: ${p => p.theme.type.label};
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        color: ${p => p.theme.text2};
        border-bottom: 1px solid ${p => p.theme.border};
        user-select: none;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
    }

    & td {
        padding: 0.5rem 0.75rem;
        font-size: ${p => p.theme.type.table};
        color: ${p => p.theme.text};
        border-bottom: 1px solid ${p => p.theme.border};
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
        font-variant-numeric: tabular-nums;
        transition: background-color 0.15s ease;
    }

    & tbody tr {
        transition: background-color 0.15s ease;

        &:hover {
            background-color: ${p => p.theme.surface3};
        }

        &:last-child td {
            border-bottom: none;
        }
    }

    & tr.isDeleted {
        background-color: ${p => p.theme.surface2};
        opacity: 0.6;

        &:hover {
            opacity: 0.75;
        }
    }

    & .header-name {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 0.3rem;
        overflow: hidden;
        min-width: 0;

        & > * {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            min-width: 0;
        }
    }

    & .resizer {
        position: absolute;
        top: 0;
        right: 0;
        width: 4px;
        height: 100%;
        background-color: ${p => p.theme.borderStrong};
        border-radius: ${p => p.theme.radius.sm};
        cursor: col-resize;
        opacity: 0;
        transition: opacity 0.2s ease;
        z-index: 1;
    }

    & th:hover .resizer,
    & .resizer.isResizing {
        opacity: 1;
    }

    & a {
        color: ${p => p.theme.accentText};
        text-decoration: none;
        font-weight: 600;
        transition: color 0.2s ease;

        &:hover {
            text-decoration: underline;
        }
    }

    & td.userPrincipalName,
    & td.mail,
    & td.proxyAddresses,
    & td.manager > p {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
`;

/* ─────────────────────── Controls ──────────────────────── */

export const TableControl = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 0.75rem;
    width: 100%;
    padding: 0.25rem 0 0.5rem;
    border-bottom: 1px solid ${p => p.theme.border};
    margin-bottom: 0.25rem;
`;

/* Compteur de résultats : placé à gauche de la barre d'outils via margin-right auto,
   TableControl étant aligné à droite. */
export const ResultCount = styled.div`
    display: flex;
    align-items: center;
    gap: 0.35rem;
    margin-right: auto;
    font-size: ${p => p.theme.type.caption};
    color: ${p => p.theme.text2};
    white-space: nowrap;

    strong {
        color: ${p => p.theme.text};
        font-variant-numeric: tabular-nums;
    }
`;

export const DivColumnVisibility = styled.div`
    position: relative;
    height: 32px;
    display: flex;
    align-items: center;
`;

export const IconMenuTableControl = styled.i`
    cursor: pointer;
    font-size: 1.2rem;
    width: 32px;
    height: 32px;
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: ${p => p.theme.radius.sm};
    color: ${p => p.theme.text2};
    transition: all 0.2s ease;

    &:hover {
        background-color: ${p => p.theme.surface3};
        color: ${p => p.theme.text};
        transform: scale(1.08);
    }
`;

export const DivSearchDropdown = styled.div`
    position: absolute;
    top: calc(100% + 8px);
    left: 50%;
    min-width: 200px;
    max-height: 220px;
    background-color: ${p => p.theme.surface};
    border: 1px solid ${p => p.theme.border};
    border-radius: ${p => p.theme.radius.md};
    box-shadow: ${p => p.theme.shadowMd};
    z-index: 20;
    padding: 0.5rem;
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    gap: 2px;

    &.active {
        opacity: 1;
        visibility: visible;
        transform: translate(-50%, 0);
        animation: ${slideDown} 0.2s ease-out;
    }

    &.inactive {
        opacity: 0;
        visibility: hidden;
        transform: translate(-50%, -8px);
        pointer-events: none;
        transition: all 0.2s ease;
    }
`;

export const DivContainerColumnRow = styled.div`
    display: flex;
    align-items: center;
    border-radius: ${p => p.theme.radius.sm};
    padding: 0.1rem 0.4rem;
    transition: background-color 0.15s ease;

    &:hover {
        background-color: ${p => p.theme.surface3};
    }

    & label {
        display: flex;
        align-items: center;
        gap: 0.6rem;
        width: 100%;
        cursor: pointer;
        font-size: 0.82rem;
        padding: 0.3rem 0;
        color: ${p => p.theme.text2};
        user-select: none;
    }

    & input[type='checkbox'] {
        accent-color: ${p => p.theme.accent};
        width: 15px;
        height: 15px;
        cursor: pointer;
    }
`;

/* ─────────────────────── Pagination ────────────────────── */

export const DivPageVisibility = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;

    & > span {
        display: flex;
        align-items: center;
        gap: 0.35rem;
        font-size: 0.82rem;
        color: ${p => p.theme.text2};
        white-space: nowrap;

        & strong {
            color: ${p => p.theme.text};
        }

        & div {
            font-size: 0.78rem;
            color: ${p => p.theme.accentText};
        }
    }
`;

export const ButtonChangePage = styled.button`
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    background-color: transparent;
    border: 1px solid ${p => p.theme.border};
    border-radius: ${p => p.theme.radius.sm};
    color: ${p => p.theme.text};
    transition: all 0.2s ease;

    &:hover:not(:disabled) {
        background-color: ${p => p.theme.surface3};
        border-color: ${p => p.theme.borderStrong};
    }

    &:active:not(:disabled) {
        transform: scale(0.95);
    }

    &:disabled {
        cursor: default;
        opacity: 0.35;
    }
`;

export const IconArrow = styled.i`
    font-size: 1.1rem;
    line-height: 1;
`;

export const SelectShowElement = styled.select`
    appearance: none;
    background-color: transparent;
    border: 1px solid ${p => p.theme.border};
    border-radius: ${p => p.theme.radius.sm};
    padding: 0.35rem 1.8rem 0.35rem 0.6rem;
    font-size: 0.82rem;
    color: ${p => p.theme.text};
    cursor: pointer;
    transition: border-color 0.2s ease;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='grey' viewBox='0 0 16 16'%3E%3Cpath d='M8 11L3 6h10z'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 0.5rem center;

    &:hover {
        border-color: ${p => p.theme.borderStrong};
    }

    &:focus {
        outline: none;
        border-color: ${p => p.theme.accent};
        background-color: ${p => p.theme.surface};
    }
`;

export const IconSort = styled.i`
    cursor: pointer;
    font-size: 1rem;
    margin-left: 0.15rem;
    color: ${p => p.theme.accentText};
    transition: color 0.2s ease, transform 0.2s ease;
    flex-shrink: 0;

    &:hover {
        color: ${p => p.theme.text};
        transform: scale(1.15);
    }
`;

/* ─────────────────────── Detail Panel ──────────────────── */

export const DetailOverlay = styled.div`
    position: fixed;
    inset: 0;
    background-color: rgba(0, 0, 0, 0.35);
    backdrop-filter: blur(3px);
    z-index: 10;
    animation: ${fadeOverlay} 0.2s ease;
`;

export const DivContainerDetail = styled.div`
    position: fixed;
    width: clamp(340px, 50vw, 700px);
    height: 75%;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background-color: ${p => p.theme.surface};
    border: 1px solid ${p => p.theme.border};
    border-radius: ${p => p.theme.radius.md};
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.18), 0 4px 12px rgba(0, 0, 0, 0.08);
    padding: 1.25rem 1.5rem;
    z-index: 11;
    overflow-y: auto;
    animation: ${fadeIn} 0.25s ease-out;

    &.nested-detail {
        width: clamp(300px, 45vw, 640px);
        z-index: 12;
    }
`;

export const DivContainerDetailContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.75rem;

    & .detail-key {
        font-weight: 600;
        font-size: 0.82rem;
        color: ${p => p.theme.text};
        margin-right: 0.3rem;
    }

    & .detail-value {
        font-size: 0.82rem;
        color: ${p => p.theme.text2};
        word-break: break-word;
    }

    & .detail-div {
        display: flex;
        align-items: baseline;
        padding: 0.25rem 0;
        border-bottom: 1px dashed ${p => p.theme.border};
    }

    & .detail-div-bis, .detail-div-nested {
        padding: 0.25rem 0;
        border-bottom: 1px dashed ${p => p.theme.border};
    }

    & .detail-div-nested:only-child {
        border-bottom: none;
    }

    & .detail-div-nested .detail-div-master > *:last-child {
        border-bottom: none;
    }

    & .detail-div-array {
        display: flex;
        flex-direction: column;
    }

    & .detail-div-master {
        display: flex;
        flex-direction: column;
        gap: 0.25rem;
    }

    & .detail-link {
        background: none;
        border: none;
        text-decoration: none;
        cursor: pointer;
        font-size: 0.88rem;
        color: ${p => p.theme.text2};
        width: fit-content;
        padding: 2px 0;
        border-bottom: 1px solid transparent;
        transition: all 0.2s ease;

        &:hover {
            color: ${p => p.theme.text};
            border-bottom-color: ${p => p.theme.accent};
        }
    }

    & .nested-object > .detail-div-master,
    & .detail-div-array > .detail-div-master {
        gap: 0;
    }

    & .nested-object .detail-div-master .detail-div {
        border: none;
    }

    & p { margin: 0; }

    & .active {
        transform: scaleY(1);
        transform-origin: top;
        height: auto;
        transition: transform 0.2s ease;
    }

    & .inactive {
        transform: scaleY(0);
        height: 0;
        overflow: hidden;
    }
`;

export const IconClose = styled.i`
    cursor: pointer;
    font-size: 1.25rem;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: ${p => p.theme.radius.sm};
    color: ${p => p.theme.text2};
    transition: all 0.2s ease;

    &:hover {
        background-color: ${p => p.theme.surface3};
        color: ${p => p.theme.text};
    }
`;

export const IconDetail = styled.i`
    cursor: pointer;
    font-size: 1.15rem;
    color: ${p => p.theme.accentText};
    padding: 0.25rem;
    border-radius: ${p => p.theme.radius.sm};
    transition: all 0.2s ease;

    &:hover {
        color: ${p => p.theme.text};
        background-color: ${p => p.theme.surface3};
        transform: scale(1.1);
    }
`;

/* ─────────────────────── Export CSV ─────────────────────── */

export const DivExportCSV = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
`;

export const ButtonExportCSV = styled.button`
    background-color: transparent;
    border: 1px solid ${p => p.theme.border};
    border-radius: ${p => p.theme.radius.sm};
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.15rem;
    color: ${p => p.theme.text2};
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover:not(:disabled) {
        background-color: ${p => p.theme.surface3};
        border-color: ${p => p.theme.borderStrong};
        color: ${p => p.theme.text};
    }

    &:active:not(:disabled) {
        transform: scale(0.95);
    }

    &:disabled {
        cursor: not-allowed;
        opacity: 0.4;
    }
`;

/* ─────────────────────── Loader ────────────────────────── */

export const DivLoader = styled.div`
    position: absolute;
    inset: 0;
    background-color: ${p => p.theme.surface}CC;
    backdrop-filter: blur(2px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10;
    border-radius: ${p => p.theme.radius.md};
    animation: ${fadeOverlay} 0.2s ease;
`;

export const DivSpinner = styled.div`
    width: 40px;
    height: 40px;
    border: 3px solid ${p => p.theme.border};
    border-top-color: ${p => p.theme.accent};
    border-radius: 50%;
    animation: ${spin} 0.7s linear infinite;
`;